import { useEffect, useRef, useState } from 'react';

const defaultIsReady = (value: unknown): boolean => value !== '' && value !== null && value !== undefined;

/**
 * A debounced copy of `value` that does not delay the page's first load.
 *
 * Filters that load asynchronously (schemes, asset classes, a default date)
 * start empty and then fill in once. That first real value is the page's
 * initial state, not a user edit, so it passes through immediately — a plain
 * debounce there is what made pages sit on "No data" for a beat before the
 * fetch even started. Every change after that (checkbox toggling, typing) is
 * debounced by `delayMs` as usual.
 *
 * `isReady` says when the value has stopped being a placeholder; by default
 * anything other than '', null or undefined.
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs: number,
  isReady: (value: T) => boolean = defaultIsReady,
): T {
  const [debounced, setDebounced] = useState(value);
  const settledRef = useRef(isReady(value));
  const isReadyRef = useRef(isReady);
  isReadyRef.current = isReady;

  useEffect(() => {
    if (!settledRef.current) {
      // Still on placeholders — follow the value without delay, and stop doing
      // so the moment it becomes real.
      settledRef.current = isReadyRef.current(value);
      setDebounced(value);
      return undefined;
    }
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  // Until settled, hand back the live value rather than the state copy: the
  // state only catches up in the effect above, and a fetch effect running in
  // this same render would otherwise see the stale placeholder.
  return settledRef.current ? debounced : value;
}
